import { BusinessObjectBase } from './base';
import type { Token } from './token';

export interface MessageListenerData {
  readonly id: string;
  readonly boundaryEventId: string;
  readonly messageName?: string;
  readonly interrupting?: boolean;
  readonly status?: string;
  readonly completedAt?: Date;
  readonly tokenId: number;
  readonly token?: Token;
}

export interface MessageListener extends Readonly<MessageListenerData> {}
export class MessageListener extends BusinessObjectBase {
  static readonly entityName = 'MessageListener' as const;
  static readonly plural = 'message-listeners';

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<MessageListener[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new MessageListener(data));
  }

  static async findOne(filter?: any): Promise<MessageListener | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new MessageListener(data) : null;
  }

  static async findById(id: string, filter?: any): Promise<MessageListener> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new MessageListener(data);
  }

  static async create(
    data: Omit<MessageListenerData, 'id'>,
  ): Promise<MessageListener> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new MessageListener(result);
  }

  static async updateById(
    id: string,
    data: Partial<MessageListenerData>,
  ): Promise<MessageListener> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const result = await this.patch<any>(url, data);
    return new MessageListener(result);
  }

  static async deleteById(id: string): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async trigger(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${MessageListener.plural}/${(this as any).id}/trigger`;
    return BusinessObjectBase.post<any>(url, options);
  }
}
